import React, { useState, useEffect } from 'react';
import { websiteAnalyticsService } from '../services/websiteAnalyticsService';
import { UserBehaviorDashboard } from '../components/Analytics/UserBehaviorDashboard';
import { ConversionFunnelVisualization } from '../components/Analytics/ConversionFunnelVisualization';
import { ButtonClickAnalytics } from '../components/Analytics/ButtonClickAnalytics';
import { SessionAnalytics } from '../components/Analytics/SessionAnalytics';
import { TrafficPatternAnalytics } from '../components/Analytics/TrafficPatternAnalytics';
import { HeatmapVisualization } from '../components/Analytics/HeatmapVisualization';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import MetricCard from '../components/Common/MetricCard';

const WebsiteAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    endDate: new Date()
  });
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'behavior' | 'funnel' | 'traffic' | 'heatmap'>('overview');
  const [heatmapPage, setHeatmapPage] = useState<string>('/');
  const [availablePages, setAvailablePages] = useState<string[]>(['/']);
  const [selectedDateRange, setSelectedDateRange] = useState<'today' | '7days' | '30days'>('7days');

  const loadAvailablePages = async () => {
    try {
      const response = await websiteAnalyticsService.getAvailablePages();
      if (response.success && response.data && response.data.length > 0) {
        const pages = response.data.map((p: any) => p.page).filter((p: string) => p);
        setAvailablePages(pages.length > 0 ? pages : ['/']);
        if (!pages.includes(heatmapPage) && pages.length > 0) {
          setHeatmapPage(pages[0]);
        }
      }
    } catch (error) {
      console.error('Failed to load available pages:', error);
    }
  };

  useEffect(() => {
    loadDashboardData();
    loadAvailablePages();
  }, [dateRange]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await websiteAnalyticsService.getDashboardData({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
      if (response.success && response.data) {
        setDashboardData(response.data);
      }
    } catch (error) {
      console.error('Failed to load website analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (range: 'today' | '7days' | '30days') => {
    const now = new Date();
    let startDate = new Date();
    switch (range) {
      case 'today':
        startDate.setHours(0, 0, 0, 0);
        break;
      case '7days':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30days':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
    }
    setSelectedDateRange(range);
    setDateRange({ startDate, endDate: now });
  };

  const handleRefresh = () => {
    loadDashboardData();
    loadAvailablePages();
  };

  if (loading && !dashboardData) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Dashboard', icon: '📊' },
    { id: 'behavior', label: 'Visitors & Pages', icon: '👥' },
    { id: 'funnel', label: 'Booking Journey', icon: '🎯' },
    { id: 'traffic', label: 'Traffic Times', icon: '📈' },
    { id: 'heatmap', label: 'Click Map', icon: '🔥' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200 px-4 md:px-6 py-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Website Analytics</h1>
            <p className="text-sm text-gray-500 mt-1">Track user behavior and website performance</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2 transition-colors"
            >
              <span className={loading ? 'animate-spin' : ''}>🔄</span>
              Refresh
            </button>
            <div className="flex bg-gray-100 rounded-lg p-1">
              {[
                { id: 'today', label: 'Today' },
                { id: '7days', label: '7 Days' },
                { id: '30days', label: '30 Days' }
              ].map((range) => (
                <button
                  key={range.id}
                  onClick={() => handleDateRangeChange(range.id as any)}
                  className={`px-3 md:px-4 py-1.5 text-sm rounded-md transition-all ${
                    selectedDateRange === range.id
                      ? 'bg-white text-blue-600 shadow-sm font-medium'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mt-4 -mb-px overflow-x-auto">
          <div className="flex gap-1 min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-4 py-2.5 text-sm font-medium rounded-t-lg flex items-center gap-2 transition-all border-b-2 ${
                  activeTab === tab.id
                    ? 'bg-gray-50 text-blue-600 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700 border-transparent hover:bg-gray-50'
                }`}
              >
                <span>{tab.icon}</span>
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="p-4 md:p-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && dashboardData && (
          <div className="space-y-6">
            {/* Key Metrics Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard
                title="Total Page Views"
                value={dashboardData.totalPageViews || 0}
                icon={<span className="text-xl">👁️</span>}
                color="blue"
              />
              <MetricCard
                title="Unique Visitors"
                value={dashboardData.uniqueVisitors || 0}
                icon={<span className="text-xl">👤</span>}
                color="green"
              />
              <MetricCard
                title="Avg Session"
                value={websiteAnalyticsService.formatDuration(dashboardData.avgSessionDuration || 0)}
                icon={<span className="text-xl">⏱️</span>}
                color="purple"
              />
              <MetricCard
                title="Bounce Rate"
                value={websiteAnalyticsService.formatPercentage(dashboardData.bounceRate || 0)}
                icon={<span className="text-xl">🚪</span>}
                color="red"
              />
            </div>

            {/* Real-time Activity */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                Real-time Activity
              </h2>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-xl border border-green-100">
                  <div className="text-3xl md:text-4xl font-bold text-green-600">
                    {dashboardData.activeUsers || 0}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Active Now</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="text-3xl md:text-4xl font-bold text-blue-600">
                    {dashboardData.activeSessions || 0}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Sessions</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-xl border border-purple-100">
                  <div className="text-3xl md:text-4xl font-bold text-purple-600">
                    {dashboardData.recentConversions || 0}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Conversions</div>
                </div>
              </div>
            </div>

            {/* Button Clicks & Sessions - Full Width Sections */}
            <div className="space-y-6">
              <ButtonClickAnalytics filters={{ startDate: dateRange.startDate, endDate: dateRange.endDate }} />
              <SessionAnalytics filters={{ startDate: dateRange.startDate, endDate: dateRange.endDate }} />
            </div>
          </div>
        )}

        {/* User Behavior Tab */}
        {activeTab === 'behavior' && (
          <UserBehaviorDashboard filters={{ startDate: dateRange.startDate, endDate: dateRange.endDate }} />
        )}

        {/* Conversion Funnel Tab */}
        {activeTab === 'funnel' && (
          <ConversionFunnelVisualization filters={{ startDate: dateRange.startDate, endDate: dateRange.endDate }} />
        )}

        {/* Traffic Patterns Tab */}
        {activeTab === 'traffic' && (
          <TrafficPatternAnalytics filters={{ startDate: dateRange.startDate, endDate: dateRange.endDate }} />
        )}

        {/* Heatmap Tab */}
        {activeTab === 'heatmap' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <label htmlFor="heatmap-page" className="text-sm font-medium text-gray-700">
                  Select Page:
                </label>
                <select
                  id="heatmap-page"
                  value={heatmapPage}
                  onChange={(e) => setHeatmapPage(e.target.value)}
                  className="flex-1 max-w-md border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {availablePages.map(page => (
                    <option key={page} value={page}>{page}</option>
                  ))}
                </select>
              </div>
            </div>
            <HeatmapVisualization page={heatmapPage} filters={{ startDate: dateRange.startDate, endDate: dateRange.endDate }} />
          </div>
        )}
      </div>
    </div>
  );
};

export default WebsiteAnalytics;
